// src/pages/offlineSales/terminal/components/OrderDiscountModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import styles from '../OfflineSalesTerminal.module.css';

const NUMPAD_KEYS = ['7','8','9','4','5','6','1','2','3','C','0','⌫'];

const OrderDiscountModal = ({ subtotal, currentDiscount, onApply, onClose }) => {
  const { t, formatCurrency } = useLocalization();
  const [mode, setMode] = useState('flat'); // 'flat' | 'percent'
  const [value, setValue] = useState(currentDiscount > 0 ? String(currentDiscount) : '');

  const numericValue = parseFloat(value) || 0;
  const preview = mode === 'flat'
    ? numericValue
    : subtotal * (numericValue / 100);

  const handleNumpad = (key) => {
    if (key === 'C') { setValue(''); return; }
    if (key === '⌫') { setValue(v => v.slice(0, -1)); return; }
    setValue(v => v + key);
  };

  const handleApply = () => {
    onApply(parseFloat(preview.toFixed(2)));
    onClose();
  };

  const handleApplyRef = useRef(handleApply);
  useEffect(() => { handleApplyRef.current = handleApply; });

  // Keyboard input
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        setValue(v => v + e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setValue(v => v.slice(0, -1));
      } else if (e.key === 'Delete') {
        e.preventDefault();
        setValue('');
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (preview > 0) handleApplyRef.current();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [preview, onClose]);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.discountModal}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t('pos.applyOrderDiscount')}</h3>
          <button onClick={onClose} className={styles.modalClose}><X size={18} /></button>
        </div>

        <div className={styles.discountModalBody}>
          {/* Mode toggle */}
          <div className={styles.discountToggle}>
            <button
              className={`${styles.toggleBtn} ${mode === 'flat' ? styles.active : ''}`}
              onClick={() => { setMode('flat'); setValue(''); }}
            >{t('pos.applyFlat')}</button>
            <button
              className={`${styles.toggleBtn} ${mode === 'percent' ? styles.active : ''}`}
              onClick={() => { setMode('percent'); setValue(''); }}
            >{t('pos.applyPercent')}</button>
          </div>

          {/* Value display */}
          <div className={styles.discountValueDisplay}>
            <span className={`${styles.discountValueText} ${!value ? styles.placeholder : ''}`}>
              {value || '0'}
            </span>
            <span className={styles.discountUnit}>{mode === 'percent' ? '%' : 'Rs.'}</span>
          </div>

          {/* Numpad */}
          <div className={styles.numpad} style={{ padding: '8px 12px' }}>
            {NUMPAD_KEYS.map(k => (
              <button
                key={k}
                className={`${styles.numpadKey} ${k === 'C' ? styles.numpadClear : k === '⌫' ? styles.numpadBack : ''}`}
                onClick={() => handleNumpad(k)}
              >
                {k}
              </button>
            ))}
          </div>

          {/* Preview */}
          {preview > 0 && (
            <p className={styles.discountPreview}>
              {t('pos.discountAmount')}: <strong>{formatCurrency(preview)}</strong>
            </p>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.actionBtnSecondary}>{t('common.cancel', 'Cancel')}</button>
          <button onClick={handleApply} disabled={preview <= 0} className={styles.chargeBtn}>
            {t('common.apply', 'Apply')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDiscountModal;
